import { useEffect, useState, useCallback } from 'react';
import { X } from 'lucide-react';
import { StatusBadge } from '../common/StatusBadge';
import { Spinner } from '../common/Spinner';
import { formatSchedule } from './JobCard';
import JobRunsTable from './JobRunsTable';
import JobCostChart from './JobCostChart';
import { useJobStore } from '../../stores/jobStore';
import type { Job, JobRun } from '../../types/jobs';

interface JobDetailModalProps {
  job: Job;
  onClose: () => void;
}

export default function JobDetailModal({ job, onClose }: JobDetailModalProps) {
  const fetchJobRuns = useJobStore((s) => s.fetchJobRuns);
  const [runs, setRuns] = useState<JobRun[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchJobRuns(job.id, 50).then((result) => {
      if (!cancelled) {
        setRuns(result);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [job.id, fetchJobRuns]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-bg-secondary max-w-3xl w-full max-h-[85vh] overflow-hidden rounded-xl border border-border-subtle flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border-subtle shrink-0">
          <h2 className="text-lg font-semibold text-text-primary">{job.name}</h2>
          <button
            onClick={onClose}
            className="text-text-muted hover:text-text-primary transition-colors p-1 rounded"
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto px-6 py-4 flex flex-col gap-6">
          {/* Overview */}
          <section>
            <h3 className="text-sm font-semibold text-text-secondary uppercase tracking-wider mb-3">
              Overview
            </h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-text-muted">Schedule Type</span>
                <p className="text-text-primary capitalize">{job.schedule_type}</p>
              </div>
              <div>
                <span className="text-text-muted">Schedule</span>
                <p className="text-text-primary">{formatSchedule(job)}</p>
              </div>
              <div>
                <span className="text-text-muted">Model</span>
                <p className="text-text-primary">{job.model}</p>
              </div>
              <div>
                <span className="text-text-muted">Session Type</span>
                <p className="text-text-primary capitalize">{job.session_type}</p>
              </div>
              <div>
                <span className="text-text-muted">Status</span>
                <div className="mt-0.5">
                  <StatusBadge
                    status={job.enabled ? 'success' : 'warning'}
                    label={job.enabled ? 'Enabled' : 'Disabled'}
                    size="sm"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Run History */}
          <section>
            <h3 className="text-sm font-semibold text-text-secondary uppercase tracking-wider mb-3">
              Run History
            </h3>
            {loading ? (
              <div className="flex justify-center py-8">
                <Spinner size="md" />
              </div>
            ) : runs.length === 0 ? (
              <p className="text-sm text-text-muted text-center py-4">No runs recorded.</p>
            ) : (
              <JobRunsTable runs={runs} jobId={job.id} />
            )}
          </section>

          {/* Cost Chart */}
          {!loading && runs.length > 0 ? (
            <section>
              <h3 className="text-sm font-semibold text-text-secondary uppercase tracking-wider mb-3">
                Cost (Last 30 Days)
              </h3>
              <JobCostChart runs={runs} />
            </section>
          ) : null}
        </div>
      </div>
    </div>
  );
}
